import { showUserAlert } from "../../utils/userErrorMessage";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { supabase } from "../../supabase/client";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import Header from "../../Component/Header";
import usePersistedUI from "../../hooks/usePersistedUI"; // ✅ 추가
import { normalizeDateRange, validateNativeDateInputs } from "../../utils/dateUtils";
import { fetchAllRows } from "../../shared/fetchAllRows";
import * as XLSX from "xlsx";

const getFirstDayOfMonth = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().split("T")[0];
};
const getToday = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const expenseFields = [
  { label: "조합비", key: "johab" },
  { label: "운영비", key: "unyoung" },
  { label: "GPS", key: "gps" },
  { label: "할부금", key: "halbu" },
  { label: "할부 잔액", key: "halbu_balance" },
  { label: "급여", key: "geubyeo" },
  { label: "4대보험", key: "4bohum" },
  { label: "통행료", key: "tonghaeng" },
  { label: "주차료", key: "joocha" },
  { label: "숙식비", key: "sooksic" },
  { label: "정비비", key: "jungbi" },
  { label: "주유비", key: "jooyu" },
  { label: "보험료", key: "bohum" },
  { label: "세차료", key: "secha" },
  { label: "출장비", key: "chooljang" },
  { label: "과태료", key: "gwatae" },
  { label: "기타비", key: "gita" },
  { label: "비고", key: "bigo" }, // 문자열
];

const isNumericField = (key) => key !== "bigo";
const numericKeys = expenseFields.filter(({ key }) => isNumericField(key)).map(({ key }) => key);
const expenseSelectColumns = [
  "id",
  "created_at",
  "date",
  "car_number",
  "human_name",
  "phone",
  "company",
  "company_name",
  ...expenseFields.map(({ key }) => key),
].join(", ");
const pageSizeOptions = [30, 40, 50, 60, 70, 80, 90, 100];
const automationFields = expenseFields.filter(({ key }) => isNumericField(key) && key !== "halbu_balance");

const makeEmptyAutomationForm = () => {
  const form = {
    expense_day: "last",
    direct_only: true,
    automation_start_date: getToday(),
  };
  automationFields.forEach(({ key }) => {
    form[key] = 0;
  });
  return form;
};

const compareCarNumber = (a = "", b = "") => {
  const aText = String(a || "");
  const bText = String(b || "");
  const aDigits = aText.replace(/\D/g, "");
  const bDigits = bText.replace(/\D/g, "");
  const aNumber = aDigits ? Number(aDigits.slice(-4)) : Number.NaN;
  const bNumber = bDigits ? Number(bDigits.slice(-4)) : Number.NaN;

  if (Number.isFinite(aNumber) && Number.isFinite(bNumber) && aNumber !== bNumber) {
    return aNumber - bNumber;
  }

  return aText.localeCompare(bText, "ko-KR", { numeric: true });
};

// 개별 행 합계(비고 제외 숫자만) + visibleFields 고려
const computeRowSum = (src, visibleFields = null) => {
  if (!src) return 0;
  return numericKeys.reduce((acc, k) => {
    // visibleFields가 넘어온 경우, 체크된 항목만 합산
    if (visibleFields && visibleFields[k] === false) return acc;
    return acc + (Number(src[k]) || 0);
  }, 0);
};

export default function CarExpenses() {
  // ❌ 기존: 개별 useState
  // const [dateRange, setDateRange] = useState({ from: getFirstDayOfMonth(), to: getToday() });
  // const [filters, setFilters] = useState({ car_number: "", human_name: "" });
  // ✅ 주유비 엑셀 업로드
  const [fuelUploading, setFuelUploading] = useState(false);
  const fileRef = React.useRef(null);
  const fromDateInputRef = useRef(null);
  const toDateInputRef = useRef(null);

  // ✅ 공용 UI 상태 훅 (기간 + 차량/기사 검색어 기억)
  const ui = usePersistedUI(
    "car-expenses",
    {
      dateRange: { from: getFirstDayOfMonth(), to: getToday() },
      filters: { 차량: "", 기사: "" },
    },
    { urlSync: true } // ?from=...&to=...&car=...&driver=... 과 동기화
  );
  const { state } = ui;

  const [data, setData] = useState([]);
  const [total, setTotal] = useState({});
  // const [grandTotal, setGrandTotal] = useState(0); // 전체 합계
  const [visibleFields, setVisibleFields] = useState(() => {
    const s = {};
    expenseFields.forEach((f) => (s[f.key] = true));
    return s;
  });
  const allChecked = useMemo(
    () => expenseFields.every(({ key }) => !!visibleFields[key]),
    [visibleFields]
  );

  const toggleAllVisible = (checked) => {
    const next = {};
    expenseFields.forEach(({ key }) => {
      next[key] = checked;
    });
    setCurrentPage(1);
    setVisibleFields(next);
  };

  // 인라인 편집/추가 상태
  const [editingId, setEditingId] = useState(null); // 현재 편집 중인 행 id
  const [draftRow, setDraftRow] = useState(null); // 편집/추가용 임시 값
  const [addingMode, setAddingMode] = useState(false); // 추가 등록 행이 표 하단에 열렸는지
  const [saving, setSaving] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [automationOpen, setAutomationOpen] = useState(false);
  const [automationForm, setAutomationForm] = useState(() => makeEmptyAutomationForm());
  const [automationSaving, setAutomationSaving] = useState(false);
  const [automationLoaded, setAutomationLoaded] = useState(false);


  const fetchAutomationSettings = async () => {
    const company = localStorage.getItem("email") || "";
    if (!company) return null;

    const { data: settings, error } = await supabase
      .from("car_expense_automation_settings")
      .select("*")
      .eq("company", company)
      .maybeSingle();

    if (error) {
      console.error("지출 자동화 설정 조회 실패:", error);
      return null;
    }

    if (settings) {
      setAutomationForm({
        ...makeEmptyAutomationForm(),
        ...settings,
        expense_day: settings.expense_day || "last",
        direct_only: settings.direct_only !== false,
      });
    }
    setAutomationLoaded(true);
    return settings;
  };

  const openAutomationSettings = async () => {
    if (!automationLoaded) await fetchAutomationSettings();
    setAutomationOpen(true);
  };

  const updateAutomationForm = (field, value) => {
    setAutomationForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveAutomationSettings = async () => {
    const company = localStorage.getItem("email") || "";
    if (!company) return showUserAlert("회사 정보가 없습니다. 다시 로그인해 주세요.");

    setAutomationSaving(true);
    try {
      const payload = {
        company,
        company_name: localStorage.getItem("companyName") || null,
        expense_day: automationForm.expense_day || "last",
        direct_only: !!automationForm.direct_only,
        automation_start_date: getToday(),
        updated_at: new Date().toISOString(),
      };

      automationFields.forEach(({ key }) => {
        payload[key] = Number(automationForm[key] || 0);
      });

      const { data: saved, error } = await supabase
        .from("car_expense_automation_settings")
        .upsert(payload, { onConflict: "company" })
        .select("*")
        .single();

      if (error) throw error;
      setAutomationForm({
        ...makeEmptyAutomationForm(),
        ...saved,
        direct_only: saved.direct_only !== false,
      });
      setAutomationLoaded(true);
      await fetchData(state.dateRange);
      showUserAlert("지출 자동화 설정이 저장되었습니다.");
      setAutomationOpen(false);
    } catch (e) {
      showUserAlert("지출 자동화 설정 저장 실패: " + (e.message || e));
    } finally {
      setAutomationSaving(false);
    }
  };

  // 차량번호로 cars에서 기사명을 찾아 draftRow.human_name 자동 입력
  const fillDriverFromCarNumber = async (carNumber) => {
    if (!carNumber) return;
    const company = localStorage.getItem("email");

    // 1) 차량번호 전체 일치
    let { data } =
      (await supabase
        .from("cars")
        .select("driver")
        .eq("company", company)
        .eq("car_number", carNumber)
        .maybeSingle?.()) ?? { data: null, error: null };

    // 2) 못 찾으면 끝4자리(car_number_only)
    if (!data) {
      const last4 = (carNumber || "").slice(-4);
      const res2 = await supabase
        .from("cars")
        .select("driver")
        .eq("company", company)
        .eq("car_number_only", last4)
        .maybeSingle?.();
      data = res2?.data ?? null;
    }

    if (data?.driver) {
      setDraftRow((prev) => (prev ? { ...prev, human_name: data.driver } : prev));
    }
  };

  const searchGeneration = useRef(0);
  useEffect(() => () => { searchGeneration.current += 1; }, []);
  const fetchData = async (dateRangeOverride = null) => {
    const generation = ++searchGeneration.current;
    const company = localStorage.getItem("email") || "";
    if (!company) {
      showUserAlert("회사 정보가 없습니다. 다시 로그인해 주세요.");
      setData([]);
      setTotal({});
      return;
    }
    const { from, to } = dateRangeOverride || state.dateRange || {};
    const likeCar = state.filters?.차량?.trim() || "";
    const likeHuman = state.filters?.기사?.trim() || "";
    try {
      const result = await fetchAllRows(() => {
        let query = supabase.from("cars-expenses").select(expenseSelectColumns)
          .eq("company", company).order("date").order("car_number").order("id");
        if (from) query = query.gte("date", from);
        if (to) query = query.lte("date", to);
        if (likeCar) query = query.ilike("car_number", `%${likeCar}%`);
        if (likeHuman) query = query.ilike("human_name", `%${likeHuman}%`);
        return query;
      });
      if (generation !== searchGeneration.current) return;
      setData(result);
      setTotal(Object.fromEntries(expenseFields.map(({ key }) => [
        key, isNumericField(key) ? result.reduce((sum, row) => sum + (Number(row[key]) || 0), 0) : null,
      ])));
    } catch (error) {
      if (generation !== searchGeneration.current) return;
      console.error("지출 조회 실패:", error?.code);
      showUserAlert("지출 조회에 실패했습니다. 잠시 후 다시 검색해 주세요.");
      setData([]);
      setTotal({});
    }
    if (generation !== searchGeneration.current) return;
    setAddingMode(false);
    setDraftRow(null);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    if (!validateNativeDateInputs([fromDateInputRef.current, toDateInputRef.current])) return;
    const nextDateRange = normalizeDateRange(state.dateRange);
    ui.setPartial({ dateRange: nextDateRange });
    setCurrentPage(1);
    fetchData(nextDateRange);
  };

  const beginEditRow = (row) => {
    if (saving) return;
    setAddingMode(false);
    setDraftRow(null);

    setEditingId(row.id);
    const draft = {
      id: row.id,
      date: row.date || getToday(),
      car_number: row.car_number || "",
      human_name: row.human_name || "",
    };
    expenseFields.forEach(({ key }) => {
      draft[key] = isNumericField(key) ? Number(row[key]) || 0 : row[key] ?? "";
    });
    setDraftRow(draft);
  };

  const beginAddRow = () => {
    if (saving) return;
    if (!addingMode) {
      setEditingId(null);
      setDraftRow(null);
      setAddingMode(true);
      setCurrentPage(Math.max(1, Math.ceil((rowsForRender.length + 1) / rowsPerPage)));
      const draft = {
        id: "NEW",
        date: getToday(),
        car_number: "",
        human_name: "",
      };
      expenseFields.forEach(({ key }) => {
        draft[key] = isNumericField(key) ? 0 : "";
      });
      setDraftRow(draft);
    } else {
      saveNewRow();
    }
  };

  const onChangeDraft = (field, value) => {
    setDraftRow((prev) => {
      const v = isNumericField(field) ? (value === "" ? "" : value) : value;
      return { ...prev, [field]: v };
    });

    // 추가 등록 모드에서 차량번호 입력 시 기사 자동 채움
    if (addingMode && field === "car_number") {
      fillDriverFromCarNumber(value);
    }
  };

  const saveEditedRow = async () => {
    if (!editingId || !draftRow) return;
    setSaving(true);
    try {
      const company = localStorage.getItem("email") || "";
      if (!company) return showUserAlert("회사 정보가 없습니다. 다시 로그인해 주세요.");

      const payload = {
        company,
        date: draftRow.date,
        car_number: draftRow.car_number,
        human_name: draftRow.human_name,
      };

      expenseFields.forEach(({ key }) => {
        payload[key] = isNumericField(key)
          ? Number(draftRow[key] || 0)
          : draftRow[key] ?? "";
      });

      const { error } = await supabase
        .from("cars-expenses")
        .update(payload)
        .eq("id", editingId)
        .eq("company", company);

      if (error) throw error;
      showUserAlert("수정이 완료되었습니다.");
      setEditingId(null);
      setDraftRow(null);
      setAddingMode(false);
      await fetchData();
    } catch (e) {
      showUserAlert("수정 실패: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteEditedRow = async () => {
    if (!editingId) return;
    if (!window.confirm("정말 이 행을 삭제할까요?")) return;
    setSaving(true);
    const company = localStorage.getItem("email") || "";
    try {
      const { error } = await supabase
        .from("cars-expenses")
        .delete()
        .eq("id", editingId)
        .eq("company", company);

      if (error) throw error;
      showUserAlert("삭제되었습니다.");
      setEditingId(null);
      setDraftRow(null);
      setAddingMode(false);
      await fetchData();
    } catch (e) {
      showUserAlert("삭제 실패: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveNewRow = async () => {
    if (!addingMode || !draftRow) return;
    if (!draftRow.date) return showUserAlert("지출일자를 입력하세요.");
    if (!draftRow.car_number) return showUserAlert("차량번호를 입력하세요.");

    setSaving(true);
    try {
      const company = localStorage.getItem("email") || "";
      if (!company) return showUserAlert("회사 정보가 없습니다. 다시 로그인해 주세요.");

      const payload = {
        company,
        date: draftRow.date,
        car_number: draftRow.car_number,
        human_name: draftRow.human_name,
      };

      expenseFields.forEach(({ key }) => {
        payload[key] = isNumericField(key)
          ? Number(draftRow[key] || 0)
          : draftRow[key] ?? "";
      });

      const { error } = await supabase
        .from("cars-expenses")
        .insert([payload]);
      if (error) throw error;

      showUserAlert("추가 등록되었습니다.");
      setAddingMode(false);
      setDraftRow(null);
      await fetchData();
    } catch (e) {
      showUserAlert("추가 등록 실패: " + e.message);
    } finally {
      setSaving(false);
    }
  };
    const parseExcelDateToYYYYMMDD = (v) => {
    // 1) 이미 "YYYY-MM-DD" 형태
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v.trim())) return v.trim();

    // 2) "YYYY/MM/DD" 또는 "YYYY.MM.DD"
    if (typeof v === "string") {
      const s = v.trim().replace(/\./g, "-").replace(/\//g, "-");
      if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
        const [y, m, d] = s.split("-").map((x) => String(x).padStart(2, "0"));
        return `${y}-${m}-${d}`;
      }
    }

    // 3) 엑셀 날짜(일련번호)
    if (typeof v === "number") {
      const dt = XLSX.SSF.parse_date_code(v);
      if (dt?.y && dt?.m && dt?.d) {
        const y = String(dt.y).padStart(4, "0");
        const m = String(dt.m).padStart(2, "0");
        const d = String(dt.d).padStart(2, "0");
        return `${y}-${m}-${d}`;
      }
    }

    return null;
  };

  const normalizeCarNumber = (v) => (v ?? "").toString().trim();
  const normalizeText = (v) => (v ?? "").toString().trim();
  const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
  const isEmptyCell = (v) => v === null || v === undefined || String(v).trim() === "";
  const parseRequiredMoney = (v) => {
    if (isEmptyCell(v)) return { ok: false, value: 0, reason: "주유비가 비어 있습니다." };
    const s = String(v).replace(/,/g, "").trim();
    const n = Number(s);
    if (!Number.isFinite(n)) return { ok: false, value: 0, reason: "주유비가 숫자가 아닙니다." };
    return { ok: true, value: n };
  };

  const applyFuelExcel = async (file) => {
    const company = localStorage.getItem("email");
    if (!company) return showUserAlert("회사 정보가 없습니다. 다시 로그인해 주세요.");

    setFuelUploading(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      const hasColumn = (...keys) => rows.some((r) => keys.some((key) => hasOwn(r, key)));
      const missingColumns = [];
      if (!hasColumn("date", "지출일자", "날짜")) missingColumns.push("date/지출일자/날짜");
      if (!hasColumn("car_number", "차량번호")) missingColumns.push("car_number/차량번호");
      if (!hasColumn("jooyu", "주유비")) missingColumns.push("jooyu/주유비");
      if (missingColumns.length > 0) {
        showUserAlert(`엑셀 컬럼에 문제가 있습니다.\n누락된 컬럼: ${missingColumns.join(", ")}`);
        return;
      }

      // ✅ 기대 컬럼명: date, car_number, jooyu, bigo
      // (한글 헤더면 아래에서 매핑해도 됨)
      const issues = [];
      const items = [];
      rows.forEach((r, index) => {
        const rowNumber = index + 2;
        const dateRaw = r.date ?? r.지출일자 ?? r.날짜;
        const carRaw = r.car_number ?? r.차량번호;
        const jooyuRaw = r.jooyu ?? r.주유비;
        const bigoRaw = r.bigo ?? r.비고;

        if (
          isEmptyCell(dateRaw) &&
          isEmptyCell(carRaw) &&
          isEmptyCell(jooyuRaw) &&
          isEmptyCell(bigoRaw)
        ) {
          return;
        }

        const rowIssues = [];
        const date = parseExcelDateToYYYYMMDD(dateRaw);
        const car_number = normalizeCarNumber(carRaw);
        const money = parseRequiredMoney(jooyuRaw);
        const bigo = normalizeText(bigoRaw);

        if (!date) rowIssues.push("날짜 형식을 확인하세요.");
        if (!car_number) rowIssues.push("차량번호가 비어 있습니다.");
        if (!money.ok) rowIssues.push(money.reason);

        if (rowIssues.length > 0) {
          issues.push({ rowNumber, message: rowIssues.join(" ") });
          return;
        }

        items.push({ rowNumber, date, car_number, jooyu: money.value, bigo });
      });

      if (issues.length > 0) {
        const rowText = issues.map((issue) => issue.rowNumber).join(", ");
        const detail = issues.map((issue) => `${issue.rowNumber}행: ${issue.message}`).join("\n");
        showUserAlert(`${rowText}행에 문제가 있습니다.\n${detail}\n\n한 행이라도 문제가 있으면 전체 업로드를 중단합니다.`);
        return;
      }

      if (items.length === 0) {
        showUserAlert("엑셀에서 업로드할 행을 찾지 못했습니다.\n(date/차량번호/주유비 컬럼을 확인하세요)");
        return;
      }

      const { data: carRows, error: carErr } = await supabase
        .from("cars")
        .select("car_number, car_number_only, driver")
        .eq("company", company);

      if (carErr) throw carErr;

      const driverByCar = new Map();
      (carRows || []).forEach((car) => {
        if (car.car_number) driverByCar.set(String(car.car_number).trim(), car.driver || "");
        if (car.car_number_only) driverByCar.set(String(car.car_number_only).trim(), car.driver || "");
      });

      const findDriverByCarNumber = (carNumber) => {
        const text = String(carNumber || "").trim();
        return driverByCar.get(text) || driverByCar.get(text.slice(-4)) || "";
      };

      const inserts = items.map((it) => ({
        company,
        date: it.date,
        car_number: it.car_number,
        human_name: findDriverByCarNumber(it.car_number),

        // 숫자필드 기본 0
        johab: 0, unyoung: 0, gps: 0, halbu: 0, halbu_balance: 0, geubyeo: 0, "4bohum": 0,
        tonghaeng: 0, joocha: 0, sooksic: 0, jungbi: 0, jooyu: it.jooyu,
        bohum: 0, secha: 0, chooljang: 0, gwatae: 0, gita: 0,

        // 비고
        bigo: it.bigo || "",
      }));

      if (inserts.length > 0) {
        const { error } = await supabase.from("cars-expenses").insert(inserts);
        if (error) throw error;
      }

      showUserAlert(`주유비 업로드 완료!\n신규추가: ${inserts.length}건`);
      await fetchData();
    } catch (e) {
      console.error(e);
      showUserAlert("엑셀 업로드 실패: " + (e.message || e));
    } finally {
      setFuelUploading(false);
      if (fileRef.current) fileRef.current.value = ""; // 같은 파일 재업로드 가능하게
    }
  };

  const onFuelExcelPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await applyFuelExcel(file);
  };

  const openFuelExcelPicker = () => {
    showUserAlert(
      "주유비 엑셀 파일은 왼쪽의 '엑셀 주유 파일 다운로드'버튼을 눌러서 받으실 수 있습니다.\n" +
        "해당 파일의 1행을 바꾸지 마시고, 2행부터 써주시면 됩니다."
    );
    fileRef.current?.click();
  };


  const renderCell = (row, key) => {
    const isEditingRow =
      editingId === row.id || (addingMode && row.id === "NEW");
    if (!isEditingRow) {
      if (key === "date" || key === "car_number" || key === "human_name") {
        return row[key] ?? "";
      }
      if (key === "bigo") {
        return row[key] ?? "";
      }
      return (Number(row[key]) || 0).toLocaleString();
    }

    const value = draftRow?.[key] ?? (isNumericField(key) ? 0 : "");
    if (key === "date") {
      return (
        <Input
          type="date"
          value={value}
          onChange={(e) => onChangeDraft("date", e.target.value)}
          disabled={saving}
        />
      );
    }
    if (key === "car_number" || key === "human_name" || key === "bigo") {
      return (
        <Input
          type="text"
          value={value}
          onChange={(e) => onChangeDraft(key, e.target.value)}
          disabled={saving}
        />
      );
    }
    return (
      <Input
        type="text"
        value={value}
        onChange={(e) => onChangeDraft(key, e.target.value)}
        disabled={saving}
        className="text-right"
      />
    );
  };
  // ✅ 현재 visibleFields 기준, 화면에 표시되는 숫자 항목만 합산한 전체 합계
  const visibleGrandTotal = useMemo(
    () => data.reduce((acc, row) => acc + computeRowSum(row, visibleFields), 0),
    [data, visibleFields]
  );
  const visibleNumericFieldCount = useMemo(
    () => expenseFields.filter(({ key }) => key !== "bigo" && visibleFields[key]).length,
    [visibleFields]
  );
  const tableColumnCount = 5 + visibleNumericFieldCount + (visibleFields.bigo ? 1 : 0);

  const rowsForRender = useMemo(() => {
    const filtered = data.filter((row) => computeRowSum(row, visibleFields) > 0);
    const sorted = [...filtered].sort((a, b) => {
      const dateCmp = (a.date || "").localeCompare(b.date || "");
      if (dateCmp !== 0) return dateCmp;
      const carCmp = compareCarNumber(a.car_number, b.car_number);
      if (carCmp !== 0) return carCmp;
      return String(a.id || "").localeCompare(String(b.id || ""));
    });
    const shouldAppendNew =
      addingMode && draftRow && draftRow.id === "NEW";
    return shouldAppendNew ? [...sorted, draftRow] : sorted;
  }, [data, visibleFields, addingMode, draftRow]);

  const totalPages = Math.max(1, Math.ceil(rowsForRender.length / rowsPerPage));
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);

  useEffect(() => {
    if (currentPage !== safeCurrentPage) {
      setCurrentPage(safeCurrentPage);
    }
  }, [currentPage, safeCurrentPage]);

  const paginatedRows = useMemo(() => {
    const start = (safeCurrentPage - 1) * rowsPerPage;
    return rowsForRender.slice(start, start + rowsPerPage);
  }, [rowsForRender, rowsPerPage, safeCurrentPage]);

  const getPrintableRow = (row) => {
    if (
      draftRow &&
      (editingId === row.id || (addingMode && row.id === "NEW"))
    ) {
      return draftRow;
    }
    return row;
  };

  const formatPrintValue = (row, key) => {
    if (key === "date" || key === "car_number" || key === "human_name" || key === "bigo") {
      return row?.[key] ?? "";
    }
    return (Number(row?.[key]) || 0).toLocaleString();
  };

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const handlePrint = () => {
    const visibleMoneyFields = expenseFields.filter(
      ({ key }) => key !== "bigo" && visibleFields[key]
    );
    const showBigo = !!visibleFields.bigo;
    const rowsToPrint = paginatedRows.map(getPrintableRow);
    const title = "지출 내역";
    const rangeText = `${state.dateRange?.from || ""} ~ ${state.dateRange?.to || ""}`;
    const filterText = [
      state.filters?.차량 ? `차량번호: ${state.filters.차량}` : "",
      state.filters?.기사 ? `기사명: ${state.filters.기사}` : "",
    ]
      .filter(Boolean)
      .join(" / ");

    const headerCells = [
      "순번",
      "지출일자",
      "차량번호",
      "기사명",
      ...visibleMoneyFields.map(({ label }) => label),
      "합계",
      ...(showBigo ? ["비고"] : []),
    ];

    const totalCells = [
      "합계",
      "총 합계",
      "",
      "",
      ...visibleMoneyFields.map(({ key }) => total[key]?.toLocaleString?.() ?? "0"),
      visibleGrandTotal.toLocaleString(),
      ...(showBigo ? [""] : []),
    ];

    const bodyRows = rowsToPrint.map((row, index) => [
      String((safeCurrentPage - 1) * rowsPerPage + index + 1),
      formatPrintValue(row, "date"),
      formatPrintValue(row, "car_number"),
      formatPrintValue(row, "human_name"),
      ...visibleMoneyFields.map(({ key }) => formatPrintValue(row, key)),
      computeRowSum(row, visibleFields).toLocaleString(),
      ...(showBigo ? [formatPrintValue(row, "bigo")] : []),
    ]);

    const renderRow = (cells, className = "") =>
      `<tr class="${className}">${cells
        .map((cell, index) => {
          const isMoneyCell =
            index >= 4 && index < 4 + visibleMoneyFields.length + 1;
          const alignClass = isMoneyCell ? "money" : index === cells.length - 1 && showBigo ? "left" : "center";
          return `<td class="${alignClass}">${escapeHtml(cell)}</td>`;
        })
        .join("")}</tr>`;

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(title)}</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 24px;
              font-family: Arial, "Malgun Gothic", sans-serif;
              color: #111827;
            }
            h1 {
              margin: 0 0 8px;
              font-size: 20px;
            }
            .meta {
              margin-bottom: 14px;
              font-size: 12px;
              color: #4b5563;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: auto;
              font-size: 11px;
            }
            th, td {
              border: 1px solid #9ca3af;
              padding: 4px 5px;
              vertical-align: middle;
              white-space: nowrap;
            }
            th {
              background: #f3f4f6;
              text-align: center;
              font-weight: 700;
            }
            .total td {
              background: #e5e7eb;
              font-weight: 700;
            }
            .center { text-align: center; }
            .left { text-align: left; white-space: normal; }
            .money { text-align: right; }
            @page { size: landscape; margin: 10mm; }
            @media print {
              body { margin: 0; }
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(title)}</h1>
          <div class="meta">
            지출일: ${escapeHtml(rangeText)}
            ${filterText ? ` / ${escapeHtml(filterText)}` : ""}
            / ${safeCurrentPage}페이지
          </div>
          <table>
            <thead>
              <tr>${headerCells.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${renderRow(totalCells, "total")}
              ${bodyRows.map((row) => renderRow(row)).join("")}
            </tbody>
          </table>
          <script>
            window.addEventListener("load", () => {
              window.focus();
              window.print();
            });
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      showUserAlert("인쇄 창을 열 수 없습니다. 브라우저의 팝업 차단을 확인해주세요.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div className="space-y-1">
      <Header />
      <div className="px-4 pt-1 pb-4">
        <h2 className="text-lg font-semibold">지출 내역</h2>

        <div className="space-y-2">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm">지출일:</span>
            <Input ref={fromDateInputRef} type="date" {...ui.bind("dateRange.from")} />
            <span>~</span>
            <Input ref={toDateInputRef} type="date" {...ui.bind("dateRange.to")} />
            <Input
              placeholder="차량번호"
              className="w-40"
              {...ui.bind("filters.차량")}
            />
            <Input
              placeholder="기사명"
              className="w-40"
              {...ui.bind("filters.기사")}
            />
            <Button onClick={handleSearch}>검색</Button>

            {/* ✅ 예제 파일 다운로드 버튼 */}
            <a
              href="/주유.xlsx"
              download
              className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium border bg-white hover:bg-gray-50"
              title="주유 엑셀 예제 파일 다운로드"
            >
              엑셀 주유 파일 다운로드
            </a>

            {/* ✅ 주유비 엑셀 업로드 */}
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={onFuelExcelPick}
            />
            <Button
              variant="outline"
              disabled={fuelUploading}
              onClick={openFuelExcelPicker}
            >
              {fuelUploading ? "업로드중..." : "주유비 엑셀 업로드"}
            </Button>
            <Button
              variant="outline"
              onClick={openAutomationSettings}
            >
              지출 자동화 세팅
            </Button>
          </div>

          <div className="flex flex-wrap gap-4 p-2">
            <label className="flex items-center gap-1 font-semibold">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={(e) => toggleAllVisible(e.target.checked)}
              />
              전체
            </label>
            {expenseFields.map(({ label, key }) => (
              <div key={key} className="flex items-center gap-2">
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={visibleFields[key]}
                    onChange={() => {
                      setCurrentPage(1);
                      setVisibleFields((prev) => ({
                        ...prev,
                        [key]: !prev[key],
                      }));
                    }}
                  />
                  {label}
                </label>
                {key === "bigo" && (
                  <select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="h-8 rounded border border-gray-300 bg-white px-2 text-sm"
                  >
                    {pageSizeOptions.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>

        <Card>
          <CardContent className="bg-white">
            <div className="relative max-h-[70vh] overflow-x-auto">
              <table className="table-auto w-full border text-sm">
                <thead className="sticky top-0 z-10 bg-gray-100 font-semibold">
                  <tr>
                    <th className="border p-2">순번</th>
                    <th className="border p-2">지출일자</th>
                    <th className="border p-2">차량번호</th>
                    <th className="border p-2">기사명</th>
                    {expenseFields
                      .filter(({ key }) => key !== "bigo")
                      .map(
                        ({ label, key }) =>
                          visibleFields[key] && (
                            <th key={key} className="border p-2">
                              {label}
                            </th>
                          )
                      )}
                    <th className="border p-2">합계</th>
                    {visibleFields["bigo"] && (
                      <th className="border p-2">비고</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {/* 합계 행 */}
                  <tr className="bg-gray-200 font-semibold">
                    <td className="border p-2 text-center">합계</td>
                    <td colSpan={3} className="border p-2 text-center">
                      총 합계
                    </td>
                    {expenseFields
                      .filter(({ key }) => key !== "bigo")
                      .map(
                        ({ key }) =>
                          visibleFields[key] && (
                            <td
                              key={key}
                              className="border p-2 text-right"
                            >
                              {total[key]?.toLocaleString?.() ?? 0}
                            </td>
                          )
                      )}
                    <td className="border p-2 text-right">
                      {visibleGrandTotal.toLocaleString()}
                    </td>
                    {visibleFields["bigo"] && (
                      <td className="border p-2 text-left"></td>
                    )}
                  </tr>

                  {/* 데이터/추가 행 */}
                  {paginatedRows.map((row, index) => (
                    <tr key={row.id ?? `tmp-${index}`}>
                      <td className="border p-2 text-center">
                        <button
                          className="text-blue-600 underline disabled:text-gray-400"
                          onClick={() => beginEditRow(row)}
                          disabled={saving || row.id === "NEW"}
                        >
                          {(safeCurrentPage - 1) * rowsPerPage + index + 1}
                        </button>
                      </td>

                      <td className="border p-2">
                        {renderCell(row, "date")}
                      </td>
                      <td className="border p-2">
                        {renderCell(row, "car_number")}
                      </td>
                      <td className="border p-2">
                        {renderCell(row, "human_name")}
                      </td>

                      {expenseFields
                        .filter(({ key }) => key !== "bigo")
                        .map(
                          ({ key }) =>
                            visibleFields[key] && (
                              <td
                                key={`${row.id}-${key}`}
                                className="border p-2 text-right"
                              >
                                {renderCell(row, key)}
                              </td>
                            )
                        )}
                      <td className="border p-2 text-right">
                        {(
                          (editingId === row.id ||
                            (addingMode && row.id === "NEW"))
                            ? computeRowSum(draftRow, visibleFields)
                            : computeRowSum(row, visibleFields)
                        ).toLocaleString()}
                      </td>
                      {visibleFields["bigo"] && (
                        <td className="border p-2 text-left">
                          {renderCell(row, "bigo")}
                        </td>
                      )}
                    </tr>
                  ))}
                  {paginatedRows.length === 0 && (
                    <tr>
                      <td
                        className="border p-4 text-center text-gray-500"
                        colSpan={tableColumnCount}
                      >
                        표시할 지출 내역이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <div className="mt-2 flex items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => setCurrentPage(1)}
            disabled={safeCurrentPage === 1}
            className="min-w-8 rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            &lt;&lt;
          </button>
          <button
            type="button"
            onClick={() => setCurrentPage(Math.max(1, safeCurrentPage - 1))}
            disabled={safeCurrentPage === 1}
            className="min-w-8 rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            &lt;
          </button>
          {Array.from({ length: totalPages }, (_, index) => {
            const page = index + 1;
            const isActive = page === safeCurrentPage;
            return (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`min-w-8 rounded border px-2 py-1 text-sm ${
                  isActive
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setCurrentPage(Math.min(totalPages, safeCurrentPage + 1))}
            disabled={safeCurrentPage === totalPages}
            className="min-w-8 rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            &gt;
          </button>
          <button
            type="button"
            onClick={() => setCurrentPage(totalPages)}
            disabled={safeCurrentPage === totalPages}
            className="min-w-8 rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            &gt;&gt;
          </button>
        </div>

        <div className="flex gap-2 mt-2">
          <Button
            onClick={saveEditedRow}
            disabled={!editingId || saving}
            className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            수정 완료
          </Button>
          <Button
            onClick={deleteEditedRow}
            disabled={!editingId || saving}
            className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            삭제
          </Button>
          <Button
            onClick={handlePrint}
            disabled={saving || paginatedRows.length === 0}
            className="ml-auto bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50"
          >
            인쇄
          </Button>
          <Button
            onClick={beginAddRow}
            disabled={!!editingId || saving}
            title={
              addingMode
                ? "입력한 값으로 추가 저장"
                : "표 맨 아래에 추가 입력칸 열기"
            }
          >
            {addingMode ? "추가 등록(저장)" : "추가 등록"}
          </Button>
        </div>
      </div>
      {automationOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded bg-white p-4 shadow-lg">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">지출 자동화 세팅</h3>
              <button
                type="button"
                className="rounded px-2 py-1 text-sm hover:bg-gray-100"
                onClick={() => setAutomationOpen(false)}
                disabled={automationSaving}
              >
                닫기
              </button>
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-semibold">
                생성 날짜
                <select
                  value={automationForm.expense_day}
                  onChange={(e) => updateAutomationForm("expense_day", e.target.value)}
                  className="h-9 rounded border border-gray-300 bg-white px-2"
                  disabled={automationSaving}
                >
                  <option value="last">말일</option>
                  {Array.from({ length: 31 }, (_, index) => {
                    const day = index + 1;
                    return (
                      <option key={day} value={String(day)}>
                        {day}일
                      </option>
                    );
                  })}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!automationForm.direct_only}
                  onChange={(e) => updateAutomationForm("direct_only", e.target.checked)}
                  disabled={automationSaving}
                />
                직접관리 기사만
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {automationFields.map(({ label, key }) => (
                <label key={key} className="block space-y-1 text-sm">
                  <span className="block font-semibold">{label}</span>
                  <Input
                    type="text"
                    value={automationForm[key] ?? 0}
                    onChange={(e) => updateAutomationForm(key, e.target.value)}
                    disabled={automationSaving}
                    className="text-right"
                  />
                </label>
              ))}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                className="bg-gray-500 hover:bg-gray-600"
                onClick={() => setAutomationOpen(false)}
                disabled={automationSaving}
              >
                취소
              </Button>
              <Button
                type="button"
                onClick={saveAutomationSettings}
                disabled={automationSaving}
              >
                {automationSaving ? "저장 중..." : "저장"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
